import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import {
    Alert,
    Box,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import {
    CloseRounded,
    OpenInNewRounded,
    RefreshRounded,
    ThumbDownOutlined,
    ThumbUpOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import moment from "moment";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxChip from "components/UI/AcxChip";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxDrawer from "components/UI/Drawer/AcxDrawer";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { Routes } from "components/Navigation/Routes";
import {
    AdminModelFeedbackRow,
    emptyFeedbackFilters,
} from "services/AdminModelFeedbackService";
import OrganizationModelFeedbackStore from "./OrganizationModelFeedbackStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";

const feedbackChip = (agreed: boolean) => (
    <AcxChip
        size="small"
        label={agreed ? "Agree" : "Disagree"}
        color={agreed ? "green" : "yellow"}
        customMaterialIcon={
            agreed ? <ThumbUpOutlined /> : <ThumbDownOutlined />
        }
    />
);
const dateLabel = (value: string) => new Date(value).toLocaleString();

const feedbackOptions = [
    { label: "All feedback", value: "" },
    { label: "Agree", value: "true" },
    { label: "Disagree", value: "false" },
];
const sourceOptions = [
    { label: "All sources", value: "" },
    { label: "Conversation details", value: "conversation" },
    { label: "Evaluation", value: "evaluation" },
];
const noteOptions = [
    { label: "All notes", value: "" },
    { label: "Has note", value: "true" },
    { label: "No note", value: "false" },
];

// Trailing 10 days, like every other admin page. Nobody wants to be the
// one screen that shows all-time data by default.
const getDefaultFeedbackFilters = () => ({
    ...emptyFeedbackFilters,
    from: moment().startOf("date").subtract(10, "days").format("YYYY-MM-DD"),
    to: moment().endOf("date").format("YYYY-MM-DD"),
});

const OrganizationModelFeedback = observer(({ id }: { id: string }) => {
    const theme = useTheme();
    const store = useMemo(() => new OrganizationModelFeedbackStore(id), [id]);
    const [draft, setDraft] = useState(getDefaultFeedbackFilters);
    const [validationError, setValidationError] = useState("");
    useEffect(() => {
        const defaultFilters = getDefaultFeedbackFilters();
        setDraft(defaultFilters);
        setValidationError("");
        const columns: IColDef[] = [
            {
                field: "actions",
                headerName: "",
                width: 120,
                sortable: false,
                renderCell: ({ row }) => (
                    <AcxButton
                        color="white"
                        fullWidth={false}
                        onClick={() =>
                            store.select(row as AdminModelFeedbackRow)
                        }
                    >
                        View details
                    </AcxButton>
                ),
            },
            {
                field: "modifiedOn",
                headerName: "Last updated",
                width: 185,
                renderCell: ({ row }) => dateLabel(row.modifiedOn),
            },
            { field: "modelName", headerName: "Model", minWidth: 180, flex: 1 },
            {
                field: "version",
                headerName: "Version",
                width: 95,
                renderCell: ({ row }) =>
                    row.version == null ? "Unknown" : `v${row.version}`,
            },
            {
                field: "agreed",
                headerName: "Feedback",
                width: 130,
                renderCell: ({ row }) => feedbackChip(row.agreed),
            },
            {
                field: "note",
                headerName: "Note",
                minWidth: 220,
                flex: 1,
                sortable: false,
                renderCell: ({ row }) => (
                    <Typography
                        noWrap
                        title={row.note ?? ""}
                        sx={{ alignSelf: "center" }}
                    >
                        {row.note || "—"}
                    </Typography>
                ),
            },
            {
                field: "userName",
                headerName: "Submitted by",
                width: 170,
                renderCell: ({ row }) => row.userName || "Unknown user",
            },
            {
                field: "audioMetadataId",
                headerName: "Interaction",
                width: 170,
                sortable: false,
                renderCell: ({ row }) => (
                    <Link
                        to={Routes.makeConversationsRoute(row.audioMetadataId)}
                    >
                        {row.audioMetadataId}
                    </Link>
                ),
            },
            {
                field: "evaluationId",
                headerName: "Source",
                width: 165,
                sortable: false,
                renderCell: ({ row }) =>
                    row.evaluationId ? "Evaluation" : "Conversation details",
            },
        ];
        void store.grid.setColumns(
            columns.map((column) => ({ ...column, filterable: false })),
        );
        store.applyFilters(defaultFilters);
        return () => store.dispose();
    }, [store]);

    const apply = (event: React.FormEvent) => {
        event.preventDefault();
        if (draft.from && draft.to && draft.from > draft.to) {
            setValidationError(
                "The end date must be on or after the start date.",
            );
            return;
        }
        if (
            draft.version &&
            (!/^\d+$/.test(draft.version) || Number(draft.version) > 4294967295)
        ) {
            setValidationError("Enter a valid whole-number version.");
            return;
        }
        setValidationError("");
        store.applyFilters(draft);
    };
    const clear = () => {
        setDraft({ ...emptyFeedbackFilters });
        setValidationError("");
        store.applyFilters(emptyFeedbackFilters);
    };
    const row = store.selected;
    // Sitting at the default range doesn't count as "filtered," otherwise
    // an org with zero feedback ever would get blamed for a filter nobody set.
    const defaultFilters = getDefaultFeedbackFilters();
    const filtered =
        !!store.filters.search ||
        !!store.filters.model ||
        !!store.filters.version ||
        !!store.filters.user ||
        !!store.filters.agreed ||
        !!store.filters.source ||
        !!store.filters.hasNote ||
        (!!store.filters.from && store.filters.from !== defaultFilters.from) ||
        (!!store.filters.to && store.filters.to !== defaultFilters.to);
    // Functional setState, not optional: onSelect fires this twice in a row
    // (from then to), and two calls off a stale `draft` closure just means
    // the second one quietly eats the first. Ask me how I know.
    const updateDraft = (field: keyof typeof draft, value: string) =>
        setDraft((prev) => ({ ...prev, [field]: value }));

    return (
        <Box
            sx={{
                p: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1400px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <OrganizationBreadcrumbs pageTitle="Model Feedback" />

            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
                sx={{ mt: 2 }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: "24px",
                            fontWeight: 600,
                            color: theme.palette.gray[700],
                        }}
                    >
                        Model Feedback
                    </Typography>
                    <Typography
                        sx={{
                            color: theme.palette.gray[500],
                            fontSize: "14px",
                            mt: 0.5,
                        }}
                    >
                        Review current user feedback on model reasoning across
                        conversations and evaluations.
                    </Typography>
                </Box>
                <AcxButton
                    color="white"
                    fullWidth={false}
                    startIcon={<RefreshRounded />}
                    loading={store.grid.isLoading}
                    onClick={() => {
                        store.select(null);
                        void store.load();
                    }}
                >
                    Refresh
                </AcxButton>
            </Stack>

            <Box
                component="form"
                onSubmit={apply}
                sx={{
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.white.main,
                    p: 2,
                }}
            >
                <Typography
                    sx={{
                        color: theme.palette.gray[700],
                        fontSize: "14px",
                        fontWeight: 600,
                        mb: 2,
                    }}
                >
                    Filter feedback
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 2,
                    }}
                >
                    {(
                        [
                            ["search", "Search notes, models or users"],
                            ["model", "Model name"],
                            ["user", "User name"],
                            ["version", "Version"],
                        ] as const
                    ).map(([field, label]) => (
                        <AcxMainTextField
                            key={field}
                            id={`feedback-${field}`}
                            labelText={label}
                            fullWidth
                            value={draft[field]}
                            onChange={(event) =>
                                updateDraft(field, event.target.value)
                            }
                        />
                    ))}
                    <Box>
                        <AcxSelect
                            id="feedback-sentiment"
                            inputLabel="Feedback"
                            options={feedbackOptions}
                            defaultValue={feedbackOptions.find(
                                (option) => option.value === draft.agreed,
                            )}
                            onChange={(option) =>
                                updateDraft("agreed", option?.value ?? "")
                            }
                            fullWidth
                        />
                    </Box>
                    <Box>
                        <AcxSelect
                            id="feedback-source"
                            inputLabel="Source"
                            options={sourceOptions}
                            defaultValue={sourceOptions.find(
                                (option) => option.value === draft.source,
                            )}
                            onChange={(option) =>
                                updateDraft("source", option?.value ?? "")
                            }
                            fullWidth
                        />
                    </Box>
                    <Box>
                        <AcxSelect
                            id="feedback-note"
                            inputLabel="Note"
                            options={noteOptions}
                            defaultValue={noteOptions.find(
                                (option) => option.value === draft.hasNote,
                            )}
                            onChange={(option) =>
                                updateDraft("hasNote", option?.value ?? "")
                            }
                            fullWidth
                        />
                    </Box>
                    <Box>
                        <AcxDateRangeInput
                            labelText="Updated from / through"
                            defaultStartDate={
                                draft.from
                                    ? moment(draft.from, "YYYY-MM-DD")
                                    : undefined
                            }
                            defaultEndDate={
                                draft.to
                                    ? moment(draft.to, "YYYY-MM-DD")
                                    : undefined
                            }
                            onSelect={(start, end) => {
                                updateDraft("from", start.format("YYYY-MM-DD"));
                                updateDraft("to", end.format("YYYY-MM-DD"));
                            }}
                            wrapperStyle={{ maxWidth: "100%" }}
                            inputStyle={{ width: "100%" }}
                            sideBar
                        />
                    </Box>
                </Box>
                <Stack direction="row" spacing={1.5} mt={2.5}>
                    <AcxButton type="submit" fullWidth={false}>
                        Apply filters
                    </AcxButton>
                    <AcxButton color="white" fullWidth={false} onClick={clear}>
                        Clear filters
                    </AcxButton>
                </Stack>
            </Box>
            {validationError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {validationError}
                </Alert>
            )}
            {store.error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <AcxButton onClick={() => void store.load()}>
                            Retry
                        </AcxButton>
                    }
                >
                    {store.error}
                </Alert>
            )}
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                aria-live="polite"
            >
                <Typography
                    sx={{ color: theme.palette.gray[600], fontSize: "14px" }}
                >
                    {store.grid.isLoading
                        ? "Loading feedback…"
                        : `${store.total} matching`}
                </Typography>
                {!store.grid.isLoading && (
                    <>
                        <AcxChip
                            size="small"
                            color="green"
                            label={`${store.agreements} agree`}
                        />
                        <AcxChip
                            size="small"
                            color="yellow"
                            label={`${store.disagreements} disagree`}
                        />
                    </>
                )}
            </Stack>
            {!store.grid.isLoading && !store.error && store.total === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {filtered
                        ? "No feedback matches these filters. Try clearing the filters."
                        : "No model feedback has been submitted for this organization."}
                </Alert>
            )}
            <Box
                sx={{
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    backgroundColor: theme.palette.white.main,
                }}
            >
                <AcxDataGrid dataGridStore={store.grid} />
            </Box>
            <AcxDrawer
                anchor="right"
                open={!!row}
                onDrawerClose={() => store.select(null)}
                collapsible="full"
                disableBackdrop={false}
                disableBackdropClick={false}
                variant="temporary"
                customPaperSx={{
                    width: { xs: "100%", sm: 520 },
                    maxWidth: "100vw",
                    backgroundColor: theme.palette.gray[50],
                }}
                content={
                    <Box
                        role="dialog"
                        aria-label="Model feedback details"
                        sx={{
                            width: { xs: "100%", sm: 520 },
                            maxWidth: "100vw",
                            minHeight: "100vh",
                            overflowWrap: "anywhere",
                            backgroundColor: theme.palette.gray[50],
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{
                                borderBottom: `1px solid ${theme.palette.gray[200]}`,
                                px: 3,
                                py: 2,
                                position: "sticky",
                                top: 0,
                                backgroundColor: theme.palette.white.main,
                                zIndex: 2,
                            }}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[700],
                                        fontSize: "18px",
                                        fontWeight: 600,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    Feedback details
                                </Typography>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: "14px",
                                        mt: 0.5,
                                    }}
                                >
                                    {row?.modelName}
                                </Typography>
                            </Box>
                            <IconButton
                                aria-label="Close feedback details"
                                size="small"
                                onClick={() => store.select(null)}
                                sx={{
                                    color: theme.palette.gray[500],
                                    mt: 0.25,
                                }}
                            >
                                <CloseRounded />
                            </IconButton>
                        </Stack>
                        {row && (
                            <Stack spacing={2} sx={{ p: 3 }}>
                                <Stack direction="row" spacing={1}>
                                    {feedbackChip(row.agreed)}
                                    <AcxChip
                                        size="small"
                                        color="gray"
                                        label={
                                            row.evaluationId
                                                ? "Evaluation"
                                                : "Conversation details"
                                        }
                                    />
                                </Stack>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme.palette.gray[200]}`,
                                        borderRadius: 1,
                                        backgroundColor:
                                            theme.palette.white.main,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: theme.palette.gray[500],
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Note
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.gray[700],
                                            mt: 0.75,
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {row.note || "No note provided."}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme.palette.gray[200]}`,
                                        borderRadius: 1,
                                        backgroundColor:
                                            theme.palette.white.main,
                                        display: "grid",
                                        gridTemplateColumns:
                                            "120px minmax(0, 1fr)",
                                        columnGap: 2,
                                        rowGap: 2,
                                        "& > :nth-of-type(odd)": {
                                            color: theme.palette.gray[500],
                                            fontSize: "13px",
                                        },
                                        "& > :nth-of-type(even)": {
                                            color: theme.palette.gray[700],
                                            fontWeight: 500,
                                            fontSize: "14px",
                                        },
                                    }}
                                >
                                    <Typography>Version</Typography>
                                    <Typography>
                                        {row.version == null
                                            ? "Unknown"
                                            : `v${row.version}`}
                                    </Typography>
                                    <Typography>Submitted by</Typography>
                                    <Typography>
                                        {row.userName || "Unknown user"}
                                    </Typography>
                                    <Typography>Created</Typography>
                                    <Typography>
                                        {dateLabel(row.createdOn)}
                                    </Typography>
                                    <Typography>Last updated</Typography>
                                    <Typography>
                                        {dateLabel(row.modifiedOn)}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 0.5 }} />
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[700],
                                        fontSize: "14px",
                                        fontWeight: 600,
                                    }}
                                >
                                    Related records
                                </Typography>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{
                                        xs: "stretch",
                                        sm: "flex-start",
                                    }}
                                >
                                    <Link
                                        to={Routes.makeConversationsRoute(
                                            row.audioMetadataId,
                                        )}
                                        style={{ textDecoration: "none" }}
                                    >
                                        <AcxButton
                                            color="white"
                                            fullWidth
                                            endIcon={<OpenInNewRounded />}
                                        >
                                            Open interaction
                                        </AcxButton>
                                    </Link>
                                    {row.evaluationId && (
                                        <Link
                                            to={Routes.makeEvaluationRoute(
                                                id,
                                                row.evaluationId,
                                            )}
                                            style={{ textDecoration: "none" }}
                                        >
                                            <AcxButton
                                                color="white"
                                                fullWidth
                                                endIcon={<OpenInNewRounded />}
                                            >
                                                Open evaluation
                                            </AcxButton>
                                        </Link>
                                    )}
                                </Stack>
                                <Typography
                                    sx={{
                                        color: theme.palette.gray[500],
                                        fontSize: "12px",
                                    }}
                                >
                                    This record contains the latest opinion.
                                    Previous edits and the original reasoning
                                    are not retained.
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                }
            />
        </Box>
    );
});

export default OrganizationModelFeedback;
